import React from 'react';

import { Picture } from '@wac-web/components/wpl/image/src/image';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button-next/new';
import { Checkbox } from '@atlaskit/checkbox';
import Heading from '@atlaskit/heading';
import FileIcon from '@atlaskit/icon/core/file';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import TasksIcon from '@atlaskit/icon/core/tasks';
import Link from '@atlaskit/link';
import { Box, Grid, Stack, Text, xcss } from '@atlaskit/primitives-next';

import { FeatureBox } from './feature-box/feature-box';
import { useLegalConsentPageAnalytics, useSubmitUserConsentWithFlags } from './hooks';
import { messages } from './messages';

const baseStyles = xcss({
	width: '800px',
	marginInline: 'auto',
});

const EXTERNAL_LINK = {
	CUSTOMER_AGREEMENT: 'https://www.atlassian.com/legal/atlassian-customer-agreement#intro',
	PRIVACY_POLICY: 'https://www.atlassian.com/legal/privacy-policy',
};

// defining in-render components above to fix "react/no-unstable-nested-components" warning
const CustomerAgreementExternalLink = (chunks: React.ReactNode) => (
	<Link href={EXTERNAL_LINK.CUSTOMER_AGREEMENT} target="_blank">
		{chunks}
	</Link>
);

const CustomerAgreementPrivacyProlicy = (chunks: React.ReactNode) => (
	<Link href={EXTERNAL_LINK.PRIVACY_POLICY} target="_blank">
		{chunks}
	</Link>
);

export const LegalConsentPage = () => {
	const { submitUserConsentWithFlags, submitIsLoading: isSubmitConsentRequestPending } =
		useSubmitUserConsentWithFlags();

	const intl = useIntl();
	const [isChecked, setIsChecked] = React.useState<boolean>(false);

	const { sendCustomerConsentButtonClickUIEvent } = useLegalConsentPageAnalytics();

	const handleCheckboxAction = () => {
		setIsChecked((current) => !current);
	};

	return (
		<Box xcss={baseStyles} paddingBlockStart="space.400">
			<Stack alignBlock="center" alignInline="center">
				<Picture
					image={{
						src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/7i5qx6qe6npbltnew2788ii8d46x8siv/t/re_w_1500,re_h_380,re_rm_stretch/AT12DQGV.auto',
						alt: '',
					}}
					style={{
						width: '800px',
						height: '203px',
					}}
				/>
				<Box paddingBlockStart="space.400" paddingBlockEnd="space.200" paddingInline="space.500">
					<Stack alignBlock="center" space="space.200" alignInline="center">
						<Heading size="large" as="h1">
							<FormattedMessage {...messages.legalConsentHeading} />
						</Heading>
						<Text align="center">
							<FormattedMessage
								{...messages.legalConsentDescription}
								values={{
									a: (chunks) => CustomerAgreementExternalLink(chunks),

									b: (chunks) => CustomerAgreementPrivacyProlicy(chunks),
								}}
							/>
						</Text>
						<Text align="center">
							<FormattedMessage {...messages.legalConsentFeatureHeadlineText} />
						</Text>

						<Grid gap="space.200" autoFlow="column">
							<FeatureBox
								heading={messages.legalConsentPortalFeature_TrackTickets_Heading}
								description={messages.legalConsentPortalFeature_TrackTickets_Description}
								icon={<TasksIcon label="" />}
							/>

							<FeatureBox
								heading={messages.legalConsentPortalFeature_PlanMigration_Heading}
								description={messages.legalConsentPortalFeature_PlanMigration_Description}
								icon={<RoadmapIcon label="" />}
							/>

							<FeatureBox
								heading={messages.legalConsentPortalFeature_ViewResources_Heading}
								description={messages.legalConsentPortalFeature_ViewResources_Description}
								icon={<FileIcon label="" />}
							/>
						</Grid>

						<Checkbox
							isChecked={isChecked}
							label={
								<Text>
									<FormattedMessage
										{...messages.legalConsentPortalFeature_LegalAgreementCheckboxText}
									/>
								</Text>
							}
							onChange={handleCheckboxAction}
							name="legal-agreement-checkbox"
							testId="legal-agreement-checkbox"
						/>
					</Stack>
				</Box>
				<Box paddingBlock="space.250" paddingInline="space.500">
					<Stack alignBlock="center" alignInline="center">
						<Button
							appearance="primary"
							isLoading={isSubmitConsentRequestPending}
							onClick={() => {
								sendCustomerConsentButtonClickUIEvent();
								submitUserConsentWithFlags();
							}}
							isDisabled={!isChecked}
							aria-label={
								!isChecked
									? intl.formatMessage(
											messages.legalConsentPortalFeature_ProceedToPortalButtonAriaLabelWhenDisabled,
									  )
									: undefined
							}
						>
							{intl.formatMessage(messages.legalConsentPortalFeature_ProceedToPortalButton)}
						</Button>
					</Stack>
				</Box>
				<Text align="center" size="small">
					<FormattedMessage {...messages.legalConsentPortalFeature_LegalAgreementFooter} />
				</Text>
			</Stack>
		</Box>
	);
};
